<template>
    <div v-if="props.sharedUsers.length > 0 || props.sharedTeams.length > 0" class="mt-0">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-4">People and teams with access</h3>
        
        <!-- Shared entities container -->
        <div class="max-h-[350px] overflow-y-auto space-y-3 pr-1">
            <!-- Shared Users -->
            <div v-for="user in props.sharedUsers" 
                :key="'shared-user-' + (user.id || user._id)"
                class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl transition-all hover:bg-gray-100/75 dark:hover:bg-gray-700/50">
                <div class="flex items-center min-w-0 mr-4">
                    <div class="flex-shrink-0">
                        <img v-if="user.avatarUrl || user.avatar_url" :src="user.avatarUrl || user.avatar_url" width="40" height="40" loading="lazy" class="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700" :alt="user.name || user.fullName || user.username || user.email || 'User'">
                        <div v-else class="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 flex items-center justify-center font-bold text-sm tracking-wider">
                            {{ getInitials(user.name || user.fullName || user.username || user.email) }}
                        </div>
                    </div>
                    <div class="ml-3 min-w-0">
                        <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ user.name || user.fullName || user.username || user.email }}</p>
                        <p v-if="(user.name || user.fullName) && (user.name || user.fullName) !== (user.email || user.username)" class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ user.email || user.username }}</p>
                    </div>
                </div>
                <div class="flex-shrink-0">
                    <RoleSelector 
                        :role="user.role"
                        @update-role="(role) => updateRole(user.id || user._id, role)"
                        @remove="() => removeUser(user.id || user._id)"
                    />
                </div>
            </div>

            <!-- Shared Teams -->
            <div v-for="team in props.sharedTeams" 
                :key="'shared-team-' + (team._id || team.id)"
                class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl transition-all hover:bg-gray-100/75 dark:hover:bg-gray-700/50">
                <div class="flex items-center min-w-0 mr-4">
                    <div class="flex-shrink-0">
                        <img v-if="team.avatarUrl" :src="team.avatarUrl" width="40" height="40" loading="lazy" class="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700" :alt="team.name">
                        <div v-else class="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center justify-center font-bold">
                            <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                            </svg>
                        </div>
                    </div>
                    <div class="ml-3 min-w-0">
                        <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ team.name }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ team.memberCount || 0 }} members</p>
                    </div>
                </div>
                <div class="flex-shrink-0">
                    <RoleSelector 
                        :role="team.role"
                        @update-role="(role) => updateTeamRole(team._id || team.id, role)"
                        @remove="() => removeTeam(team._id || team.id)"
                    />
                </div>
            </div>
        </div>
    </div>
    <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
        <svg class="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <p class="text-sm">This playlist hasn't been shared with anyone yet.</p>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import RoleSelector from './RoleSelector.vue';

const props = defineProps({
    sharedUsers: {
        type: Array,
        default: () => []
    },
    sharedTeams: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update-role', 'remove-user', 'remove-team', 'update-team-role']);

const updateRole = (userId, role) => {
    emit('update-role', { userId, role });
};

const removeUser = (userId) => {
    emit('remove-user', userId);
};

const removeTeam = (teamId) => {
    emit('remove-team', teamId);
};

const updateTeamRole = (teamId, role) => {
    emit('update-role', { teamId, role });
};

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};
</script> 